import { Component, HostListener, computed, inject, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import {
  CEI_SIDENAV_COLLAPSED_WIDTH,
  CEI_SIDENAV_INTERACTION_EXPANDABLE_ICONS,
  CEI_APP_TEMPLATE_FOOTER_PLACEMENT_CONTENT,
  CeiAppShellLayoutComponent,
  CeiPersonalDataMenuComponent,
  CeiSobreVersoesService,
  CeiToolbarUserAreaComponent,
  isCeiLayoutMobileViewport,
  type CeiMenuExternalLink,
  type CeiPersonalDataMenuItem,
} from '@cei-ufg/ngx-arquitetura-cei-frontend';
import { AutenticacaoService, SessionContextService } from '@cei-ufg/ngx-cei-sso-client';
import {
  CEI_ADMIN_MENU_DADOS_PESSOAIS,
  CEI_ADMIN_MENU_SAIR,
  CEI_ADMIN_MENU_SOBRE,
  PersonalDataMenuService,
} from './services/personal-data-menu.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    CeiAppShellLayoutComponent,
    CeiPersonalDataMenuComponent,
    CeiToolbarUserAreaComponent,
  ],
  templateUrl: './app.html',
  styleUrls: ['./app.scss'],
})
export class App {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly autenticacaoService = inject(AutenticacaoService);
  private readonly personalDataMenuService = inject(PersonalDataMenuService);
  private readonly sobreVersoesService = inject(CeiSobreVersoesService);
  protected readonly sessionContext = inject(SessionContextService);

  protected readonly title = signal('cei-admin-panel-frontend');
  protected readonly isMobileViewport = signal(false);
  protected readonly sidenavInteractionMode = CEI_SIDENAV_INTERACTION_EXPANDABLE_ICONS;
  protected readonly sidenavExpanded = signal(false);
  protected readonly CEI_SIDENAV_COLLAPSED_WIDTH = CEI_SIDENAV_COLLAPSED_WIDTH;
  protected readonly CEI_APP_TEMPLATE_FOOTER_PLACEMENT_CONTENT =
    CEI_APP_TEMPLATE_FOOTER_PLACEMENT_CONTENT;

  protected readonly adminMenuLinks: CeiMenuExternalLink[] = [
    { label: 'Início', url: '/admin', icone: 'home', publicAccess: true },
    { label: 'Usuários', url: '/admin/pessoas', icone: 'people' },
    { label: 'Grupos', url: '/admin/grupos', icone: 'groups' },
    { label: 'Permissões', url: '/admin/permissoes', icone: 'lock' },
    { label: 'Aplicações', url: '/admin/aplicacoes', icone: 'apps' },
    { label: 'Entidades', url: '/admin/entidades', icone: 'business' },
    { label: 'Recursos', url: '/admin/recursos', icone: 'dashboard' },
    { label: 'Recursos x Permissões', url: '/admin/recursos/permissoes', icone: 'link' },
    { label: 'Recursos x Grupos', url: '/admin/recursos/grupos', icone: 'link' },
    { label: 'Grupos x Recursos', url: '/admin/grupos/recursos', icone: 'link' },
  ];
  protected readonly userLogin = this.sessionContext.login;
  protected readonly userId = this.sessionContext.userId;
  protected readonly pessoaId = this.sessionContext.pessoaId;
  protected readonly menuAberto = signal(false);
  protected readonly personalMenuItems = computed(() =>
    this.personalDataMenuService.buildMenuItems(this.pessoaId(), this.userId()),
  );

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.syncViewportLayout();
    }
  }

  @HostListener('window:resize')
  protected syncViewportLayout(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const mobile = isCeiLayoutMobileViewport(window.innerWidth);
    this.isMobileViewport.set(mobile);

    if (mobile) {
      this.sidenavExpanded.set(false);
    }
  }

  protected togglePersonalMenu(event: Event): void {
    event.stopPropagation();
    this.menuAberto.update((open) => !open);
  }

  protected onPersonalMenuItemClick(item: CeiPersonalDataMenuItem): void {
    const action = this.personalDataMenuService.resolveItemAction(item);

    if (action === CEI_ADMIN_MENU_DADOS_PESSOAIS) {
      this.menuAberto.set(false);
      const pessoaId = this.pessoaId();
      const usuarioId = this.userId();
      if (pessoaId != null) {
        void this.router.navigate(['/admin/pessoas/atualizar', pessoaId]);
      } else if (usuarioId != null) {
        void this.router.navigate(['/admin/pessoas/atualizar'], {
          queryParams: { usuarioId },
        });
      }
      return;
    }

    if (action === CEI_ADMIN_MENU_SOBRE) {
      this.menuAberto.set(false);
      this.sobreVersoesService.abrirDialogo();
      return;
    }

    if (action === CEI_ADMIN_MENU_SAIR) {
      this.logout();
      return;
    }

    this.menuAberto.set(false);
  }

  protected logout(): void {
    this.menuAberto.set(false);
    this.autenticacaoService.logout({ motivo: 'usuario' }).subscribe();
  }

  @HostListener('document:click', ['$event'])
  protected closeMenusOnOutsideClick(event: MouseEvent): void {
    if (this.menuAberto()) {
      this.menuAberto.set(false);
    }

    if (this.isMobileViewport() && this.sidenavExpanded()) {
      const target = event.target as HTMLElement | null;
      if (target?.closest('.app-sidenav') || target?.closest('cei-sidenav')) {
        return;
      }

      this.sidenavExpanded.set(false);
    }
  }
}
